import { BUILD } from "./building.ts";
import { NATURAL_CHESTS } from "./loot.ts";
import { SIMPATIA_CHESTS, type SimpatiaChestKind } from "./simpatias.ts";

/**
 * Modelos das estruturas (F48): bancada, bigorna, piso, alicerce e baús viram modelos do pipeline
 * (pacote `estruturas` de `tools/assets`). Só visual: ids, colisores, protocolo e servidor não mudam.
 * Cada forma cabe na caixa da malha procedural de hoje (estações e baús) ou na caixa de colisão da peça
 * da grade (piso e alicerce). Um modelo por forma; os baús dividem o mesmo, com as ferragens na cor de
 * cada um (a mesma tinta por máscara dos itens, F47). Forno, panela, paredes, portas, rampas, telhados,
 * estacadas e as estruturas do mundo continuam procedurais.
 */
export interface StructureShapeInfo {
  /**
   * Caixa do modelo (m, largura em x × altura × profundidade em z), centrada em XZ e com o pé em
   * y = −`sink`: o alvo do ajuste no pipeline.
   */
  readonly box: readonly [number, number, number];
  /** `contain`: escala uniforme até caber na caixa; `stretch`: cada eixo esticado até ela. */
  readonly mode: "contain" | "stretch";
  /**
   * No `contain`, a altura vezes este fator depois da escala uniforme, sem passar da caixa: o modelo
   * que, inteiro na caixa, ficaria baixo demais.
   */
  readonly heightScale?: number;
  /** Metros abaixo da base da estrutura (o piso e o alicerce ficam abaixo do nível da peça). */
  readonly sink?: number;
  /** Partes à parte do corpo, que se mexem (a tampa do baú gira na dobradiça). */
  readonly parts?: readonly StructurePart[];
}

export type StructurePart = "tampa";

/**
 * Formas de estrutura com modelo. Estações e baús na caixa da malha procedural de hoje; piso e alicerce
 * na caixa de colisão da peça (`pieceBoxes`): a laje de `BUILD.floorThickness` abaixo do nível e o bloco
 * de `BUILD.foundationDepth`, numa célula de `BUILD.cell`.
 */
export const STRUCTURE_SHAPES = {
  // Tampo de 1,3 × 0,75 m a 0,92 m, com ferramenta em cima (até 1 m).
  bancada: { box: [1.3, 1, 0.75], mode: "stretch" },
  // Toco, bloco, mesa e bico (para +x): 1,1 m de comprimento e 0,87 m de altura. A bigorna do pack (com
  // o pedestal) cabe pelo comprimento e ficaria com a mesa a 0,54 m, na altura do joelho: 25 % mais alta.
  bigorna: { box: [1.1, 0.87, 0.76], mode: "contain", heightScale: 1.25 },
  // Corpo, tampa e fecho do baú procedural (o fecho sai 2,5 cm na frente).
  bau: { box: [0.98, 0.64, 0.65], mode: "contain", parts: ["tampa"] },
  piso: { box: [BUILD.cell, BUILD.floorThickness, BUILD.cell], mode: "stretch", sink: BUILD.floorThickness },
  alicerce: { box: [BUILD.cell, BUILD.foundationDepth, BUILD.cell], mode: "stretch", sink: BUILD.foundationDepth },
} as const satisfies Record<string, StructureShapeInfo>;

export type StructureShape = keyof typeof STRUCTURE_SHAPES;

/** Formas na ordem estável (manifesto do pipeline e testes). */
export const STRUCTURE_SHAPE_IDS = Object.keys(STRUCTURE_SHAPES) as StructureShape[];

export interface StructureLook {
  readonly shape: StructureShape;
  /**
   * Cor (sRGB) das partes tingíveis do modelo (as ferragens do baú), mantendo o claro e o escuro do
   * atlas; sem ela, as cores do pack.
   */
  readonly tint?: string;
}

/**
 * Ferragens dos Baús de Bênção pela raridade (F48), na cor do corpo do baú procedural de antes: o Achado
 * em bronze, o de Bênção azul, o Ornamentado roxo e o Encantado dourado.
 */
export const CHEST_TINTS: Readonly<Record<SimpatiaChestKind, string>> = {
  achado: "#A8784A",
  simpatia: "#3E6FA8",
  caprichado: "#7A4FA0",
  encantado: "#C8A23A",
};

/**
 * Ferragens dos baús naturais (F18), na cor da cinta do baú procedural de onde ele está: latão no
 * Acampamento, ferro na Fortaleza e na Caverna, ferrugem no Naufrágio.
 */
const NATURAL_TINTS: Readonly<Record<keyof typeof NATURAL_CHESTS, string>> = {
  acampamento: "#B89A5A",
  engenho: "#3E444C",
  gruta: "#3E444C",
  destrocos: "#8A4B2A",
};

/**
 * Forma e tinta por id de estrutura (o `itemId` da `Structure`). O Baú de Madeira do jogador fica com as
 * ferragens do pack; os naturais e os de Bênção, com a cor deles.
 */
export const STRUCTURE_LOOKS: Readonly<Record<string, StructureLook>> = {
  bancada: { shape: "bancada" },
  bigorna: { shape: "bigorna" },
  "piso-tabuas": { shape: "piso" },
  "alicerce-pedra": { shape: "alicerce" },
  "bau-madeira": { shape: "bau" },
  ...Object.fromEntries(Object.entries(NATURAL_CHESTS).map(([kind, c]) => [c.item, { shape: "bau", tint: NATURAL_TINTS[kind as keyof typeof NATURAL_CHESTS] }])),
  ...Object.fromEntries(Object.values(SIMPATIA_CHESTS).map((c) => [c.item, { shape: "bau", tint: CHEST_TINTS[c.kind] }])),
};
